import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvaluationStore } from '../../store/evaluationStore';
import { Button } from '../ui/Button/Button';
import { WeightConfigModal } from '../ui/WeightConfigModal/WeightConfigModal';
import { CSVExportModal } from '../ui/CSVExportModal/CSVExportModal';
import { StepNavigation } from '../StepNavigation/StepNavigation';
import { DownloadIcon, EditIcon, PenIcon, ExternalLinkIcon, PreviewIcon } from '../Icons/Icons';
import type { ReviewData, CriterionWeight } from '../../types';
import { pdfExportApi } from '../../api/pdfExport';
import { formatDate } from '../../utils/dateFormatter';
import { apiClient } from '../../api/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ProcessingAnimation } from '../ui/ProcessingAnimation/ProcessingAnimation';
import { generatePDF, downloadPDF } from '../../api/pdfGenerator';

export const ReportGeneration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { 
    products, 
    criteria, 
    setCurrentStep,
    getEvaluationPosition,
    getGrades,
    getRubricData,
    getOverallEvaluationSettings,
    setOverallEvaluationSettings,
    updateProduct,
    currentExaminationId,
    saveDataAsync
  } = useEvaluationStore();
  
  const [selectedProduct, setSelectedProduct] = useState<ReviewData | null>(
    products.length > 0 ? products[0] : null
  );
  
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  
  // State for details/comments editing
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [editingDetail, setEditingDetail] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isGeneratingComment, setIsGeneratingComment] = useState(false);
  
  // デバッグ用ログ
  console.log('ReportGeneration isGeneratingPDF:', isGeneratingPDF);
  
  // PDF export limits state
  const [pdfExportStatus, setPdfExportStatus] = useState<{
    can_export: boolean;
    current_count: number;
    max_count: number | null;
    remaining: number | null;
  } | null>(null);
  
  // Check if we're in PDF generation mode
  const [isPDFMode, setIsPDFMode] = useState(false);

  // Load PDF export status on component mount
  useEffect(() => {
    const loadPdfExportStatus = async () => {
      try {
        const status = await pdfExportApi.getExportStatus();
        setPdfExportStatus(status);
      } catch (error) {
        console.error('Failed to load PDF export status:', error);
      }
    };
    
    loadPdfExportStatus();
  }, []);

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedProduct) return;
      
      const currentIndex = products.indexOf(selectedProduct);
      
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        e.preventDefault();
        setSelectedProduct(products[currentIndex - 1]);
      } else if (e.key === 'ArrowRight' && currentIndex < products.length - 1) {
        e.preventDefault();
        setSelectedProduct(products[currentIndex + 1]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProduct, products, setSelectedProduct]);

  const handlePrev = async () => {
    await saveDataAsync();
    setCurrentStep(5); // Back to rubric generation
  };

  // Simple markdown converter
  const renderMarkdown = (text: string): string => {
    if (!text) return '';
    
    // Convert markdown to HTML
    const html = text
      // Headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Lists
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      // Line breaks
      .replace(/\n/g, '<br />');
      
    return html;
  };

  // Detail editing functions
  const startEditingDetail = () => {
    setEditingDetail(selectedProduct?.reviewComment || '');
    setIsEditingDetail(true);
  };

  const saveDetailEdit = () => {
    if (selectedProduct) {
      const productIndex = products.findIndex(p => p.reviewTitle === selectedProduct.reviewTitle);
      if (productIndex >= 0) {
        updateProduct(productIndex, {
          ...selectedProduct,
          reviewComment: editingDetail
        });
        setSelectedProduct({
          ...selectedProduct,
          reviewComment: editingDetail
        });
      }
    }
    setIsEditingDetail(false);
  };

  const cancelDetailEdit = () => {
    setEditingDetail(selectedProduct?.reviewComment || '');
    setIsEditingDetail(false);
  };

  // Generate AI comment based on rubric descriptions
  const generateEvaluationComment = async () => {
    if (!selectedProduct || !currentExaminationId) return;
    
    setIsGeneratingComment(true);
    
    try {
      // Collect rubric descriptions for current product
      const rubricDescriptions = criteria.map((criterion, criterionIndex) => {
        const rubricDescription = getRubricDescription(selectedProduct, criterionIndex);
        const grade = getCriterionBasedEvaluation(selectedProduct, criterionIndex);
        
        return {
          criterionTitle: criterion.title,
          description: rubricDescription?.description || '',
          grade: grade?.name || 'N/A'
        };
      }).filter(item => item.description); // Only include criteria with descriptions
      
      if (rubricDescriptions.length === 0) {
        alert(t('report.noRubricDescriptions'));
        return;
      }
      
      // Call API to generate comment
      const response = await apiClient.generateEvaluationComment({
        examinationId: currentExaminationId,
        productTitle: selectedProduct.reviewTitle,
        rubricDescriptions,
        currentComment: editingDetail
      });
      
      // Set the generated comment
      setEditingDetail(response.comment);
    } catch (error) {
      console.error('Failed to generate comment:', error);
      alert(t('report.commentGenerationFailed'));
    } finally {
      setIsGeneratingComment(false);
    }
  };

  // Preview display toggle
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };


  // Backend PDF generation handler
  const handleDownloadPDFBackend = async () => {
    if (products.length === 0) return;
    
    if (!currentExaminationId) {
      alert(t('report.pdfGenerationFailed'));
      return;
    }

    setIsGeneratingPDF(true);
    
    try {
      // Generate PDF on backend
      // Since products don't have IDs in frontend, we'll generate all products
      const pdfBlob = await generatePDF({
        examinationId: currentExaminationId
        // productIds omitted - backend will generate for all products
      });
      
      // Download the PDF
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `evaluation-report-${timestamp}.pdf`;
      downloadPDF(pdfBlob, filename);
      
      // Update export status if needed
      if (pdfExportStatus && pdfExportStatus.max_count !== null) {
        const updatedStatus = await pdfExportApi.getExportStatus();
        setPdfExportStatus(updatedStatus);
      }
    } catch (error) {
      console.error(t('report.pdfGenerationError'), error);
      alert(t('report.pdfGenerationFailed'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Original frontend PDF generation handler (kept as fallback)
  const handleDownloadPDF = async () => {
    if (products.length === 0) return;

    // PDF export limits check disabled for now
    // if (pdfExportStatus && !pdfExportStatus.can_export) {
    //   const message = pdfExportStatus.max_count 
    //     ? t('report.pdfExportLimit', { count: pdfExportStatus.max_count, planName: t('plan.pro') })
    //     : t('report.pdfExportError');
    //   alert(message);
    //   return;
    // }

    setIsGeneratingPDF(true);
    setIsPDFMode(true);

    try {
      // Skip PDF export tracking for now
      // if (pdfExportStatus && pdfExportStatus.max_count !== null) {
      //   const exportResult = await pdfExportApi.recordExport();
      //   if (!exportResult.success) {
      //     alert(exportResult.error || t('report.pdfGenerationFailed'));
      //     setIsGeneratingPDF(false);
      //     return;
      //   }

      //   // Update local status
      //   const updatedStatus = await pdfExportApi.getExportStatus();
      //   setPdfExportStatus(updatedStatus);
      // }

      // Create a new PDF instance
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width (mm)
      const pageHeight = 297; // A4 height (mm)
      const margin = 10; // Margin between reports
      
      // Temporarily store current selection
      const originalSelectedProduct = selectedProduct;
      let isFirstProduct = true;

      // Generate PDF for each product
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        
        // Update selected product to render its report
        setSelectedProduct(product);
        
        // Wait for state update and re-render
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const reportElement = document.getElementById('report-card');
        if (!reportElement) {
          console.error(`Report card not found for product: ${product.reviewTitle}`);
          continue;
        }

        // まず、ルーブリックセクションと総合評価統計を一時的に非表示にする
        const rubricSection = reportElement.querySelector('.rubric-section') as HTMLElement;
        const summaryStats = reportElement.querySelector('.summary-stats') as HTMLElement;
        
        if (rubricSection) {
          rubricSection.style.display = 'none';
        }
        
        if (summaryStats) {
          summaryStats.style.display = 'none';
        }

        // メインコンテンツ（ルーブリックと統計以外）をキャプチャ
        const mainCanvas = await html2canvas(reportElement, {
          scale: 1, // スケールを下げてメモリ使用量を削減
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: reportElement.scrollWidth,
          height: reportElement.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          logging: false,
        });

        // ルーブリックセクションを再表示（総合評価統計は非表示のまま）
        if (rubricSection) {
          rubricSection.style.display = 'block';
        }

        // 新しいページを追加（最初の製品以外）
        if (!isFirstProduct) {
          pdf.addPage();
        }
        isFirstProduct = false;

        // メインコンテンツをPDFに追加（JPEG形式で圧縮）
        const mainImgHeight = (mainCanvas.height * imgWidth) / mainCanvas.width;
        const mainImgData = mainCanvas.toDataURL('image/jpeg', 0.7); // 70%品質のJPEG
        pdf.addImage(mainImgData, 'JPEG', 0, 0, imgWidth, mainImgHeight);

        // ルーブリックセクションを処理
        if (rubricSection) {
          // まず、ルーブリックセクション全体を表示してキャプチャ
          rubricSection.style.display = 'block';
          
          // 3個目以降のルーブリックを一時的に非表示
          const rubricTables = rubricSection.querySelectorAll('.rubric-table');
          for (let k = 2; k < rubricTables.length; k++) {
            (rubricTables[k] as HTMLElement).style.display = 'none';
          }
          
          // 最初の2個のルーブリックを含むキャプチャ
          const withFirstRubrics = await html2canvas(reportElement, {
            scale: 1, // スケールを下げてメモリ使用量を削減
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: reportElement.scrollWidth,
            height: reportElement.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            logging: false,
          });
          
          // 最初のページに追加（すでに追加済みのメインコンテンツを置き換え）
          // JsPDFの型定義の問題を回避
          const pdfInternal = pdf as any;
          pdf.deletePage(pdfInternal.internal.getNumberOfPages());
          if (!isFirstProduct) {
            pdf.addPage();
          }
          const fullImgHeight = (withFirstRubrics.height * imgWidth) / withFirstRubrics.width;
          pdf.addImage(withFirstRubrics.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, fullImgHeight);
          
          // 3個目以降のルーブリックを表示に戻す
          for (let k = 2; k < rubricTables.length; k++) {
            (rubricTables[k] as HTMLElement).style.display = 'block';
          }
          
          // 3個目以降を2個ずつ別ページに追加
          for (let j = 2; j < rubricTables.length; j += 2) {
            pdf.addPage();
            
            // 一時的なコンテナを作成（元のスタイルを保持）
            const tempContainer = document.createElement('div');
            tempContainer.className = 'report-card rubric-only-container';
            tempContainer.style.cssText = 'padding: 20px; background-color: #ffffff; width: 794px;'; // A4 width in pixels
            
            // スタイルシートをコピー
            const styleSheets = document.querySelectorAll('style, link[rel="stylesheet"]');
            styleSheets.forEach(sheet => {
              tempContainer.appendChild(sheet.cloneNode(true));
            });
            
            // ルーブリックセクションのラッパーを作成
            const rubricWrapper = document.createElement('div');
            rubricWrapper.className = 'rubric-section';
            rubricWrapper.innerHTML = '<h3>' + t('report.rubricTitle') + '</h3>';
            tempContainer.appendChild(rubricWrapper);
            
            // 2個のルーブリックをコンテナに追加
            const rubric1 = rubricTables[j].cloneNode(true) as HTMLElement;
            rubricWrapper.appendChild(rubric1);
            
            if (j + 1 < rubricTables.length) {
              const rubric2 = rubricTables[j + 1].cloneNode(true) as HTMLElement;
              rubricWrapper.appendChild(rubric2);
            }
            
            document.body.appendChild(tempContainer);
            
            // ルーブリックをキャプチャ
            const rubricCanvas = await html2canvas(tempContainer, {
              scale: 1, // スケールを下げてメモリ使用量を削減
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false,
            });
            
            // PDFに追加
            const rubricImgHeight = (rubricCanvas.height * imgWidth) / rubricCanvas.width;
            pdf.addImage(rubricCanvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, rubricImgHeight);
            
            // 一時コンテナを削除
            document.body.removeChild(tempContainer);
          }
        }
        
        // 総合評価統計を再表示
        if (summaryStats) {
          summaryStats.style.display = 'block';
        }
      }

      // Restore original selection
      setSelectedProduct(originalSelectedProduct);

      // Download PDF with all evaluations
      const timestamp = new Date().toISOString().split('T')[0];
      pdf.save(`${t('report.allEvaluations')}_${timestamp}.pdf`);
    } catch (error) {
      console.error(t('report.pdfGenerationError'), error);
      alert(t('report.pdfGenerationFailed'));
    } finally {
      setIsGeneratingPDF(false);
      setIsPDFMode(false);
    }
  };

  // Enable/disable overall evaluation settings
  const handleToggleOverallEvaluation = () => {
    const currentSettings = getOverallEvaluationSettings();
    if (currentSettings?.enabled) {
      // Disable
      setOverallEvaluationSettings({
        enabled: false,
        weights: []
      });
    } else {
      // Enable (with default settings)
      const defaultWeights: CriterionWeight[] = criteria.map((_, index) => ({
        criterionIndex: index,
        weight: 100 / criteria.length,
        maxPoints: 100
      }));
      setOverallEvaluationSettings({
        enabled: true,
        weights: defaultWeights
      });
    }
  };

  // Save weight settings
  const handleSaveWeights = (weights: CriterionWeight[]) => {
    setOverallEvaluationSettings({
      enabled: true,
      weights
    });
  };

  // Calculate overall score
  const calculateOverallScore = (product: ReviewData) => {
    const settings = getOverallEvaluationSettings();
    if (!settings?.enabled || settings.weights.length === 0) return null;

    const productIndex = products.findIndex(p => p.reviewTitle === product.reviewTitle);
    let totalScore = 0;
    let totalMaxScore = 0;

    for (const weight of settings.weights) {
      const position = getEvaluationPosition(productIndex, weight.criterionIndex);
      if (position) {
        // Convert position (0-100) to points
        const score = (position.x / 100) * weight.maxPoints;
        totalScore += score * (weight.weight / 100);
        totalMaxScore += weight.maxPoints * (weight.weight / 100);
      }
    }

    return totalMaxScore > 0 ? {
      score: totalScore,
      maxScore: totalMaxScore,
      percentage: (totalScore / totalMaxScore) * 100
    } : null;
  };

  // Calculate group average
  const calculateGroupAverage = (criterionIndex: number) => {
    const allPositions = products
      .map((_, productIndex) => getEvaluationPosition(productIndex, criterionIndex))
      .filter(position => position !== null);
    
    if (allPositions.length === 0) return 0;
    
    const averagePosition = allPositions.reduce((sum, pos) => sum + pos!.x, 0) / allPositions.length;
    return averagePosition;
  };

  // Get criterion-based evaluation grade
  const getCriterionBasedEvaluation = (product: ReviewData, criterionIndex: number) => {
    const criterionGrades = getGrades(criterionIndex);
    const productIndex = products.findIndex(p => p.reviewTitle === product.reviewTitle);
    const position = getEvaluationPosition(productIndex, criterionIndex);
    
    if (!position || criterionGrades.length === 0) return null;
    
    // Determine grade based on position
    const sortedGrades = [...criterionGrades].sort((a, b) => b.gradeXStart - a.gradeXStart);
    for (const grade of sortedGrades) {
      if (position.x >= grade.gradeXStart) {
        return {
          id: `${criterionIndex}-${grade.gradeLetter}`,
          name: grade.gradeLetter,
          description: t('report.nthGrade', { grade: grade.gradeLetter }),
          value: position.x
        };
      }
    }
    
    return sortedGrades[sortedGrades.length - 1] ? {
      id: `${criterionIndex}-${sortedGrades[sortedGrades.length - 1].gradeLetter}`,
      name: sortedGrades[sortedGrades.length - 1].gradeLetter,
      description: t('report.nthGrade', { grade: sortedGrades[sortedGrades.length - 1].gradeLetter }),
      value: position.x
    } : null;
  };

  // Get rubric description
  const getRubricDescription = (product: ReviewData, criterionIndex: number) => {
    const rubricData = getRubricData();
    if (!rubricData || criterionIndex >= rubricData.length) return null;

    const criterionGrades = getGrades(criterionIndex);
    const productIndex = products.findIndex(p => p.reviewTitle === product.reviewTitle);
    const position = getEvaluationPosition(productIndex, criterionIndex);
    
    if (!position || criterionGrades.length === 0) return null;
    
    // Determine grade based on position
    const sortedGrades = [...criterionGrades].sort((a, b) => b.gradeXStart - a.gradeXStart);
    let selectedGrade = sortedGrades[sortedGrades.length - 1]; // Default: lowest grade
    
    for (const grade of sortedGrades) {
      if (position.x >= grade.gradeXStart) {
        selectedGrade = grade;
        break;
      }
    }

    // Search for corresponding cell in rubric matrix
    const criterionRubric = rubricData[criterionIndex];
    if (!criterionRubric) return null;

    const rubricCell = criterionRubric.find(cell => cell.grade === selectedGrade.gradeLetter);
    return rubricCell ? {
      grade: rubricCell.grade,
      description: rubricCell.description,
      level: rubricCell.level
    } : null;
  };

  // Calculate relative position within group
  const getRelativePosition = (product: ReviewData, criterionIndex: number) => {
    const productIndex = products.findIndex(p => p.reviewTitle === product.reviewTitle);
    const currentPosition = getEvaluationPosition(productIndex, criterionIndex);
    
    if (!currentPosition) return { position: 0, total: 0, percentile: 0 };

    // Get and sort positions of all products
    const allPositions = products
      .map((_, idx) => getEvaluationPosition(idx, criterionIndex))
      .filter(pos => pos !== null)
      .sort((a, b) => b!.x - a!.x);

    const position = allPositions.findIndex(pos => pos!.x === currentPosition.x) + 1;
    const total = allPositions.length;
    const percentile = total > 0 ? Math.round(((total - position) / total) * 100) : 0;

    return { position, total, percentile };
  };

  const getGradeColor = (grade: { name: string } | null) => {
    if (!grade) return '#e5e7eb';
    
    const colors: { [key: string]: string } = {
      'A': 'var(--color-grade-a)',
      'B': 'var(--color-grade-b)',
      'C': 'var(--color-grade-c)',
      'D': 'var(--color-grade-d)',
    };
    
    return colors[grade.name] || '#6b7280';
  };

  const getPercentileColor = (percentile: number) => {
    if (percentile >= 80) return 'var(--color-grade-a)';
    if (percentile >= 60) return 'var(--color-grade-b)';
    if (percentile >= 40) return 'var(--color-grade-c)';
    return 'var(--color-grade-d)';
  };

  if (!selectedProduct) {
    return (
      <div className="report-generation">
        <h2>{t('report.title')}</h2>
        <div className="no-products">
          <p>{t('report.noSubjects')}</p>
        </div>
        <StepNavigation
          currentStep={6}
          canProceed={false}
          onNext={() => {}}
          onPrev={handlePrev}
          prevLabel={t('report.backToRubric')}
        />
      </div>
    );
  }

  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{t('report.title')}</h2>
      </div>
      
      <div className="report-generation">
        {/* Product selection */}
        <div className="product-selector">
          <div className="product-selector-controls">
            <select 
              className="product-dropdown"
              value={products.indexOf(selectedProduct)}
              onChange={(e) => setSelectedProduct(products[Number(e.target.value)])}
            >
              {products.map((product, index) => (
                <option key={index} value={index}>
                  {product.reviewTitle}
                </option>
              ))}
            </select>
            
            <div className="product-nav-buttons">
              <button 
                className="nav-btn nav-btn--prev"
                onClick={() => {
                  const currentIndex = products.indexOf(selectedProduct);
                  if (currentIndex > 0) {
                    setSelectedProduct(products[currentIndex - 1]);
                  }
                }}
                disabled={products.indexOf(selectedProduct) === 0}
              >
                ← {t('common.prev')}
              </button>
              
              <span className="product-counter">
                {products.indexOf(selectedProduct) + 1} / {products.length}
              </span>
              
              <button 
                className="nav-btn nav-btn--next"
                onClick={() => {
                  const currentIndex = products.indexOf(selectedProduct);
                  if (currentIndex < products.length - 1) {
                    setSelectedProduct(products[currentIndex + 1]);
                  }
                }}
                disabled={products.indexOf(selectedProduct) === products.length - 1}
              >
                {t('common.next')} →
              </button>
            </div>
          </div>
        </div>

      {/* Compact report card */}
      <div className="report-card" id="report-card">
        {/* Header - Compact */}
        <div className="report-header">
          <div className="header-main">
            <h1>{t('report.evaluationNotice')}</h1>
            <div className="evaluation-date">{t('report.evaluationDate')}: {formatDate(new Date())}</div>
          </div>
          <div className="product-info">
            <h2>{selectedProduct.reviewTitle}</h2>
            
            {/* Details/comments editing area */}
            <div className="product-detail-section">
              <div className="detail-header">
                <label className="detail-label">{t('report.comments')}</label>
                <div className="detail-actions">
                  {!isEditingDetail && (
                    <button 
                      className="edit-detail-btn no-print"
                      onClick={startEditingDetail}
                      title={t('report.editComments')}
                    >
                      <PenIcon />
                    </button>
                  )}
                  {selectedProduct.reviewLink && (
                    <>
                      <button 
                        className="action-btn action-btn--preview"
                        onClick={togglePreview}
                        title={showPreview ? t('report.hidePreview') : t('report.showPreview')}
                      >
                        <PreviewIcon />
                      </button>
                      <a
                        href={selectedProduct.reviewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn action-btn--external"
                        title={t('preview.openInNewTab')}
                      >
                        <ExternalLinkIcon />
                      </a>
                    </>
                  )}
                </div>
              </div>
              
              {isEditingDetail ? (
                <div className="detail-edit-form">
                  <textarea
                    className="detail-textarea"
                    value={editingDetail}
                    onChange={(e) => setEditingDetail(e.target.value)}
                    placeholder={t('report.commentsPlaceholder')}
                    rows={4}
                  />
                  <div className="detail-edit-actions">
                    <Button size="sm" variant="primary" onClick={saveDetailEdit}>
                      {t('common.save')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelDetailEdit}>
                      {t('common.cancel')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      onClick={generateEvaluationComment}
                      disabled={isGeneratingComment}
                      loading={isGeneratingComment}
                    >
                      {t('report.generateComment')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="detail-display"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedProduct.reviewComment || t('report.commentsEmpty')) }}
                />
              )}
            </div>

            {/* Preview area */}
            {selectedProduct.reviewLink && showPreview && (
              <div className="preview-section">
                <h4>{t('report.preview')}</h4>
                <div className="preview-container">
                  <iframe
                    src={selectedProduct.reviewLink}
                    className="preview-iframe"
                    title={`${selectedProduct.reviewTitle} - ${t('report.preview')}`}
                    sandbox="allow-same-origin allow-scripts"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main evaluation section - left/right split layout */}
        <div className="evaluation-main">
          {/* Left side: detailed evaluation */}
          <div className="evaluation-details">
            <h3>{t('report.criteriaEvaluation')}</h3>
            <div className="criteria-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('report.criterionColumn')}</th>
                    <th>{t('report.gradeColumn')}</th>
                    <th>{t('report.rankColumn')}</th>
                    <th>{t('report.descriptionColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {criteria.map((criterion, criterionIndex) => {
                    const grade = getCriterionBasedEvaluation(selectedProduct, criterionIndex);
                    const position = getRelativePosition(selectedProduct, criterionIndex);
                    const rubricDescription = getRubricDescription(selectedProduct, criterionIndex);
                    
                    return (
                      <tr key={criterionIndex}>
                        <td className="criterion-name">
                          <div className="criterion-cell-content">
                            <div className="criterion-title">{criterion.title}</div>
                            {criterion.description && criterion.description.length > 0 && (
                              <div className="criterion-desc">
                                {(() => {
                                  const fullText = criterion.description.join(' ');
                                  return fullText.length > 100 ? fullText.substring(0, 100) + '...' : fullText;
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="grade-cell">
                          <div className="grade-cell-content">
                            <div 
                              className="grade-badge-compact"
                              style={{ backgroundColor: getGradeColor(grade) }}
                            >
                              {grade?.name || 'N/A'}
                            </div>
                            <div className="grade-value-compact">
                              {grade?.value?.toFixed(0) || '-'}
                            </div>
                          </div>
                        </td>
                        <td className="position-cell">
                          <div className="position-cell-content">
                            <div className="rank-compact">{t('report.rankFormat', { position: position.position, total: position.total })}</div>
                            <div 
                              className="percentile-compact"
                              style={{ color: getPercentileColor(position.percentile) }}
                            >
                              {t('report.topPercentile', { percent: 100 - position.percentile })}
                            </div>
                          </div>
                        </td>
                        <td className="description-cell">
                          {rubricDescription?.description ? (
                            <div className="rubric-text-compact">
                              {rubricDescription.description.length > 50 
                                ? `${rubricDescription.description.substring(0, 50)}...`
                                : rubricDescription.description
                              }
                            </div>
                          ) : (
                            <span className="no-description">{t('report.noDescription')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right side: summary and visualization */}
          <div className="evaluation-summary">
            <h3>{t('report.overallEvaluation')}</h3>
            
            {/* Compact radar chart */}
            <div className="radar-chart-compact">
              <svg viewBox="0 0 240 240" className="radar-svg-compact">
                {/* Background grid */}
                {[20, 40, 60, 80].map(radius => (
                  <polygon
                    key={radius}
                    points={criteria.map((_, index) => {
                      const angle = (index * 2 * Math.PI) / criteria.length - Math.PI / 2;
                      const x = 120 + Math.cos(angle) * radius * 0.8;
                      const y = 120 + Math.sin(angle) * radius * 0.8;
                      return `${x},${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="0.5"
                  />
                ))}
                
                {/* Axis lines */}
                {criteria.map((_, index) => {
                  const angle = (index * 2 * Math.PI) / criteria.length - Math.PI / 2;
                  const x2 = 120 + Math.cos(angle) * 80;
                  const y2 = 120 + Math.sin(angle) * 80;
                  return (
                    <line
                      key={index}
                      x1="120"
                      y1="120"
                      x2={x2}
                      y2={y2}
                      stroke="#e5e7eb"
                      strokeWidth="0.5"
                    />
                  );
                })}
                
                {/* Group average polygon */}
                <polygon
                  points={criteria.map((_, criterionIndex) => {
                    const averagePosition = calculateGroupAverage(criterionIndex);
                    const angle = (criterionIndex * 2 * Math.PI) / criteria.length - Math.PI / 2;
                    const radius = (averagePosition / 100) * 80;
                    const x = 120 + Math.cos(angle) * radius;
                    const y = 120 + Math.sin(angle) * radius;
                    return `${x},${y}`;
                  }).join(' ')}
                  fill="rgba(156, 163, 175, 0.15)"
                  stroke="#9ca3af"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                />
                
                {/* Personal evaluation polygon */}
                <polygon
                  points={criteria.map((_, criterionIndex) => {
                    const grade = getCriterionBasedEvaluation(selectedProduct, criterionIndex);
                    const value = grade?.value || 0;
                    const angle = (criterionIndex * 2 * Math.PI) / criteria.length - Math.PI / 2;
                    const radius = (value / 100) * 80;
                    const x = 120 + Math.cos(angle) * radius;
                    const y = 120 + Math.sin(angle) * radius;
                    return `${x},${y}`;
                  }).join(' ')}
                  fill="rgba(59, 130, 246, 0.2)"
                  stroke="var(--color-info-light)"
                  strokeWidth="2"
                />
                
                {/* Criterion labels */}
                {criteria.map((criterion, criterionIndex) => {
                  const angle = (criterionIndex * 2 * Math.PI) / criteria.length - Math.PI / 2;
                  const labelX = 120 + Math.cos(angle) * 100;
                  const labelY = 120 + Math.sin(angle) * 100;
                  
                  return (
                    <text
                      key={criterionIndex}
                      x={labelX}
                      y={labelY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="8"
                      fontWeight="500"
                      fill="#374151"
                    >
                      {criterion.title}
                    </text>
                  );
                })}
              </svg>
            </div>
            
            {/* Radar chart legend */}
            <div className="radar-legend">
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: 'rgba(59, 130, 246, 0.3)', border: '2px solid var(--color-info-light)' }}></div>
                <span>{t('report.personalEvaluation')}</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', border: '1px dashed #9ca3af' }}></div>
                <span>{t('report.groupAverage')}</span>
              </div>
            </div>
            
            {/* Summary statistics - based on overall evaluation settings */}
            <div className="summary-stats no-print-stats">
              {/* <div className="stat-item">
                <div className="stat-label">Average Grade</div>
                <div className="stat-value">
                  {(() => {
                    const validGrades = criteria
                      .map((_, i) => getCriterionBasedEvaluation(selectedProduct, i))
                      .filter(g => g !== null);
                    if (validGrades.length === 0) return 'N/A';
                    const avgValue = validGrades.reduce((sum, g) => sum + (g?.value || 0), 0) / validGrades.length;
                    // Calculate grade from average value
                    const grades = getGrades(0);
                    const sortedGrades = [...grades].sort((a, b) => b.gradeXStart - a.gradeXStart);
                    for (const grade of sortedGrades) {
                      if (avgValue >= grade.gradeXStart) {
                        return grade.gradeLetter;
                      }
                    }
                    const lastGrade = sortedGrades[sortedGrades.length - 1];
                    return (lastGrade?.gradeLetter && lastGrade.gradeLetter.trim().length > 0) ? lastGrade.gradeLetter : 'N/A';
                  })()}
                </div>
              </div> */}
              
              {/* Overall evaluation (shown only when configured) */}
              {(() => {
                const settings = getOverallEvaluationSettings();
                const overallScore = calculateOverallScore(selectedProduct);
                
                if (settings?.enabled === true && overallScore) {
                  return (
                    <div className="stat-item overall-stat">
                      <div className="stat-label">
                        {t('report.summary')}
                        <div className="stat-controls no-print">
                          <button
                            className="config-button"
                            onClick={() => setIsWeightModalOpen(true)}
                            title={t('report.configureWeights')}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="config-button disable-button"
                            onClick={handleToggleOverallEvaluation}
                            title={t('report.disableOverallEvaluation')}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="stat-value">
                        {t('report.pointsFormat', { score: overallScore.score.toFixed(1), maxScore: overallScore.maxScore.toFixed(1) })}
                        <div className="stat-sub">{t('report.percentageFormat', { percentage: overallScore.percentage.toFixed(1) })}</div>
                      </div>
                    </div>
                  );
                } else if (settings?.enabled === false) {
                  // Hide when explicitly disabled
                  return null;
                } else {
                  return (
                    <div className="stat-item no-print">
                      <div className="stat-label">{t('report.overallEvaluation')}</div>
                      <div className="stat-value">
                        <button
                          className="enable-overall-button"
                          onClick={handleToggleOverallEvaluation}
                        >
                          {t('report.enableOverallEvaluation')}
                        </button>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>

        {/* Footer information */}
        <div className="report-footer">
          <div className="footer-note">
            {t('report.footerNote')}
          </div>
        </div>

        {/* Rubric display */}
        <div className="rubric-section">
          <h3>{t('report.rubricTitle')}</h3>
          {criteria.map((criterion, criterionIndex) => {
            const rubricData = getRubricData();
            const criterionRubric = rubricData?.[criterionIndex];
            
            return (
              <div key={criterionIndex} className={`rubric-table ${criterionIndex >= 2 && criterionIndex % 2 === 0 ? 'page-break-before' : ''}`}>
                <h4>{criterion.title}</h4>
                {criterion.description && criterion.description.length > 0 && (
                  <p className="rubric-criterion-desc">{criterion.description.join(' ')}</p>
                )}
                {criterionRubric && criterionRubric.length > 0 ? (
                  <table className="rubric-grid">
                    <thead>
                      <tr>
                        {criterionRubric
                          .sort((a, b) => b.level - a.level)
                          .map((cell, cellIndex) => {
                            const evaluation = getCriterionBasedEvaluation(selectedProduct, criterionIndex);
                            const isHighlighted = evaluation?.name === cell.grade;
                            return (
                              <th key={cellIndex} className={isHighlighted ? 'highlighted-cell' : ''}>
                                {cell.grade}
                              </th>
                            );
                          })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {criterionRubric
                          .sort((a, b) => b.level - a.level)
                          .map((cell, cellIndex) => {
                            const evaluation = getCriterionBasedEvaluation(selectedProduct, criterionIndex);
                            const isHighlighted = evaluation?.name === cell.grade;
                            return (
                              <td key={cellIndex} className={isHighlighted ? 'highlighted-cell' : ''}>
                                {cell.description}
                              </td>
                            );
                          })}
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p className="no-rubric">{t('report.noRubric')}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* PDF limit information */}
      {pdfExportStatus && pdfExportStatus.max_count && (
        <div className="pdf-limit-info">
          <p>
            {t('report.pdfExportStatus', { current: pdfExportStatus.current_count, max: pdfExportStatus.max_count })}
            {pdfExportStatus.remaining && pdfExportStatus.remaining > 0 && (
              <span>{t('report.pdfExportRemaining', { remaining: pdfExportStatus.remaining })}</span>
            )}
          </p>
          {pdfExportStatus.remaining === 0 && (
            <p className="limit-reached">
              {t('report.pdfExportLimitReached', { planName: t('plan.pro') })}
            </p>
          )}
        </div>
      )}

      {/* PDF Generation Loading Animation */}
      {isGeneratingPDF && (
        <ProcessingAnimation
          title={t('report.pdfGeneration.generating')}
          description={t('report.pdfGeneration.generatingDescription')}
          steps={[
            t('report.pdfGeneration.step1'),
            t('report.pdfGeneration.step2'),
            t('report.pdfGeneration.step3')
          ]}
          currentStep={1}
        />
      )}

      {/* Action buttons */}
      <div className="report-actions">
        <Button
          variant="primary"
          leftIcon={<DownloadIcon />}
          onClick={handleDownloadPDFBackend}
          disabled={products.length === 0 || isGeneratingPDF}
          loading={isGeneratingPDF}
        >
          {isGeneratingPDF 
            ? t('report.generatingPdf')
            : t('report.downloadAllPdf', { count: products.length })}
        </Button>
        <Button
          variant="secondary"
          leftIcon={<DownloadIcon />}
          onClick={() => setIsCsvModalOpen(true)}
        >
          {t('csvExport.export')}
        </Button>
      </div>

      <StepNavigation
        currentStep={7}
        canProceed={false}
        onNext={() => {}}
        onPrev={handlePrev}
        prevLabel={t('report.backToQuick')}
      />

      {/* Weight configuration modal */}
      <WeightConfigModal
        isOpen={isWeightModalOpen}
        onClose={() => setIsWeightModalOpen(false)}
        criteria={criteria}
        initialWeights={getOverallEvaluationSettings()?.weights}
        onSave={handleSaveWeights}
      />

      {/* CSV Export modal */}
      <CSVExportModal
        isOpen={isCsvModalOpen}
        onClose={() => setIsCsvModalOpen(false)}
        products={products}
        criteria={criteria}
        getEvaluationPosition={getEvaluationPosition}
        getGrades={getGrades}
        getRubricData={getRubricData}
      />

      <style>{`
        .report-generation {
          max-width: 100%;
          margin: 0 auto;
          padding: 1rem;
        }

        .product-selector {
          margin-bottom: 1rem;
        }

        .product-selector h2 {
          margin-bottom: 0.5rem;
          color: #1f2937;
          font-size: 1.25rem;
        }

        .product-selector-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .product-dropdown {
          flex: 1;
          max-width: 400px;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 1rem;
          font-weight: 500;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .product-dropdown:hover {
          border-color: var(--color-info-light);
        }
        
        .product-dropdown:focus {
          outline: none;
          border-color: var(--color-info-light);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-info-light) 10%, transparent);
        }
        
        .product-nav-buttons {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .nav-btn {
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        
        .nav-btn:hover:not(:disabled) {
          border-color: var(--color-info-light);
          background: #eff6ff;
          color: var(--color-info-light);
        }
        
        .nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .product-counter {
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
        }

        .report-card {
          background: white;
          border-radius: 0.5rem;
          border: 1px solid #e5e7eb;
          padding: 1.5rem;
          margin-bottom: 1rem;
          font-size: 0.875rem;
        }

        .report-header {
          margin-bottom: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 1rem;
        }

        .header-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .report-header h1 {
          font-size: 1.125rem;
          color: #1f2937;
          margin: 0;
          font-weight: 500;
        }

        .evaluation-date {
          color: #6b7280;
          font-size: 0.75rem;
        }

        .product-info h2 {
          font-size: 1.375rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          font-weight: 600;
        }

        .product-detail-section {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin: 0.75rem 0;
        }

        .detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .detail-label {
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
        }

        .detail-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .edit-detail-btn {
          background: transparent;
          border: none;
          border-radius: 6px;
          padding: 0.375rem;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .edit-detail-btn svg {
          width: 16px;
          height: 16px;
        }

        .edit-detail-btn:hover {
          color: #6b7280;
          background: rgba(107, 114, 128, 0.05);
        }
        
        .preview-toggle-btn {
          background: green;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.75rem;
          color:rgb(255, 255, 255);
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .preview-toggle-btn:hover {
          border-color: #3b82f6;
          color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
        }

        .detail-edit-form {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .detail-textarea {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          line-height: 1.4;
          resize: vertical;
          min-height: 80px;
          transition: border-color 0.2s ease;
        }

        .detail-textarea:focus {
          outline: none;
          border-color: var(--color-info-light);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-info-light) 10%, transparent);
        }

        .detail-edit-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .detail-display {
          background: white;
          padding: 0.75rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          font-size: 0.875rem;
          line-height: 1.2;
          color: #374151;
          min-height: 60px;
          word-break: break-word;
        }
        
        .detail-display h1,
        .detail-display h2,
        .detail-display h3 {
          margin: 0.2rem 0;
          color: #1f2937;
          line-height: 1;
        }
        
        .detail-display h1 { font-size: 1rem; font-weight: 700; }
        .detail-display h2 { font-size: 0.9375rem; font-weight: 600; }
        .detail-display h3 { font-size: 0.875rem; font-weight: 600; }
        
        .detail-display ul {
          margin: 0;
          padding: 0;
          line-height: 0.8;
          list-style: none;
        }
        
        .detail-display li {
          margin: 0;
          padding: 0;
          padding-left: 10px;
          position: relative;
        }
        
        .detail-display li::before {
          content: "・";
          position: absolute;
          left: 0;
        }
        
        .detail-display strong {
          font-weight: 600;
          color: #1f2937;
        }
        
        .detail-display em {
          font-style: italic;
        }
        
        .detail-display br {
          margin: 0;
          line-height: 0.8;
        }

        .preview-section {
          margin-top: 1rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
        }

        .preview-section h4 {
          margin: 0 0 0.75rem 0;
          font-size: 1rem;
          color: #374151;
        }

        .preview-container {
          position: relative;
          width: 100%;
          height: 400px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          overflow: hidden;
          background: white;
        }

        .preview-iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        .product-info p {
          color: #6b7280;
          margin: 0;
          font-size: 0.8rem;
        }

        .evaluation-main {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 1.5rem;
        }

        .evaluation-details h3,
        .evaluation-summary h3 {
          color: #1f2937;
          margin: 0 0 1rem 0;
          font-size: 1rem;
        }

        .criteria-table {
          overflow-x: auto;
        }

        .criteria-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        .criteria-table th,
        .criteria-table td {
          padding: 0.5rem;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: middle;
        }

        .criteria-table th {
          background: #f9fafb;
          font-weight: 600;
          color: #374151;
          font-size: 0.75rem;
        }
        
        .criteria-table tbody tr {
          transition: background-color 0.2s ease;
        }
        
        .criteria-table tbody tr:hover {
          background-color: #f9fafb;
        }
        
        .criteria-table tbody tr td {
          min-height: 100px;
          height: auto;
          padding: 8px 12px;
          vertical-align: middle;
        }

        .criterion-name {
          min-width: 120px;
          max-width: 20vw;
          height: auto;
          vertical-align: middle;
        }

        .criterion-title {
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .criterion-desc {
          color: #6b7280;
          font-size: 0.7rem;
          line-height: 1.2;
          word-wrap: break-word;
          word-break: break-word;
          overflow-wrap: break-word;
          white-space: normal;
        }

        .grade-cell {
          text-align: center;
          min-width: 60px;
        }
        
        .grade-cell-content,
        .position-cell-content,
        .criterion-cell-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
        }
        
        .criterion-cell-content {
          align-items: flex-start;
        }

        .grade-badge-compact {
          display: inline-block;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          color: white;
          font-weight: bold;
          font-size: 0.75rem;
          line-height: 24px;
          text-align: center;
          margin: 0 0 4px 0;
        }

        .grade-value-compact {
          font-size: 0.7rem;
          color: #6b7280;
        }

        .position-cell {
          text-align: center;
          min-width: 60px;
        }

        .rank-compact {
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 2px 0;
          font-size: 0.75rem;
        }

        .percentile-compact {
          font-weight: 600;
          font-size: 0.7rem;
        }

        .description-cell {
          max-width: 200px;
        }

        .rubric-text-compact {
          color: #374151;
          line-height: 1.3;
          font-size: 0.75rem;
        }

        .no-description {
          color: #9ca3af;
        }

        .radar-chart-compact {
          margin-bottom: 1rem;
        }

        .radar-legend {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-top: 0.5rem;
          margin-bottom: 1rem;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: #374151;
        }

        .legend-color {
          width: 16px;
          height: 12px;
          border-radius: 2px;
        }

        .radar-svg-compact {
          width: 100%;
          height: 200px;
        }

        .summary-stats {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .stat-item {
          background: #f9fafb;
          padding: 0.75rem;
          border-radius: 0.375rem;
          text-align: center;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.7rem;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-size: 1.125rem;
          font-weight: bold;
          color: #1f2937;
        }

        .stat-sub {
          font-size: 0.75rem;
          font-weight: normal;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        .config-button {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 0.25rem;
          margin-left: 0.5rem;
          border-radius: 0.25rem;
          transition: all 0.2s ease;
        }

        .config-button:hover {
          color: #374151;
          background: #f3f4f6;
        }

        .pdf-limit-info {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 1rem;
          margin: 1rem 0;
          text-align: center;
        }

        .pdf-limit-info p {
          margin: 0.5rem 0;
          color: #374151;
        }

        .limit-reached {
          color: #dc2626 !important;
          font-weight: 600;
        }

        .config-button svg {
          width: 0.875rem;
          height: 0.875rem;
        }

        .stat-controls {
          display: flex;
          gap: 0.25rem;
          margin-left: 0.5rem;
        }

        .disable-button {
          color: #ef4444 !important;
          font-weight: bold;
          font-size: 1rem;
        }

        .disable-button:hover {
          background: #fef2f2 !important;
          color: #dc2626 !important;
        }

        .enable-overall-button {
          background: var(--color-info-light);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .enable-overall-button:hover {
          background: var(--color-info-dark);
        }

        @media print {
          .product-selector,
          .report-actions,
          .step-navigation,
          .preview-toggle-btn,
          .detail-actions,
          .no-print,
          .product-nav-buttons {
            display: none !important;
          }

          .report-card {
            box-shadow: none;
            border: 1px solid #000;
            margin: 0;
            padding: 1rem;
            page-break-after: avoid;
          }

          .evaluation-main {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .radar-chart-compact {
            display: none;
          }

          .criteria-table table {
            font-size: 0.7rem;
          }

          .criteria-table th,
          .criteria-table td {
            padding: 0.25rem;
          }

          .report-header h1 {
            font-size: 1.125rem;
          }

          .evaluation-details h3,
          .evaluation-summary h3 {
            font-size: 0.875rem;
          }
        }

        @media (max-width: 1024px) {
          .evaluation-main {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
        }

        @media (max-width: 768px) {
          .report-generation {
            padding: 0.75rem;
          }
          
          .product-selector-controls {
            flex-direction: column;
            align-items: stretch;
          }
          
          .product-dropdown {
            max-width: none;
          }
          
          .product-nav-buttons {
            justify-content: space-between;
            width: 100%;
          }
          
          .report-card {
            padding: 1rem;
          }

          .header-main {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .criteria-table {
            overflow-x: scroll;
          }

          .summary-stats {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 480px) {
          .report-generation {
            padding: 0.5rem;
          }
          
          .report-card {
            padding: 0.75rem;
          }
          
          .report-header h1 {
            font-size: 1.125rem;
          }

          .summary-stats {
            grid-template-columns: 1fr;
          }
        }

        /* Rubric display styles */
        .rubric-section {
          margin-top: 1.5rem;
          padding: 1rem;
          border-top: 2px solid #e5e7eb;
        }

        .rubric-section h3 {
          color: #374151;
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .rubric-table {
          margin-bottom: 1.5rem;
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .rubric-table h4 {
          color: #1f2937;
          font-size: 1rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          text-align: left;
          break-after: avoid;
          page-break-after: avoid;
        }

        .rubric-criterion-desc {
          color: #6b7280;
          font-size: 0.875rem;
          margin: -0.25rem 0 0.75rem 0;
          line-height: 1.4;
        }

        .rubric-grid {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .rubric-grid th {
          background-color: #f3f4f6;
          padding: 0.5rem;
          text-align: center;
          border: 1px solid #d1d5db;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .rubric-grid td {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          font-size: 0.875rem;
          line-height: 1.4;
          vertical-align: top;
        }

        .no-rubric {
          color: #6b7280;
          font-style: italic;
          font-size: 0.875rem;
        }

        /* Page break for rubrics */
        .page-break-before {
          page-break-before: always;
        }
        
        /* PDF output styles */
        @media print {
          .report-actions {
            display: none !important;
          }
          
          .product-selector {
            display: none !important;
          }
          
          .report-card {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 1rem !important;
          }
          
          /* PDF layout adjustments */
          .evaluation-main {
            grid-template-columns: 1fr !important;
          }
          
          /* Hide only overall evaluation stats, keep radar chart */
          .overall-stat,
          .no-print-stats {
            display: none !important;
          }
          
          .rubric-table {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          .rubric-table h4 {
            break-after: avoid;
            page-break-after: avoid;
          }
          
          .rubric-grid {
            break-inside: avoid;
          }
          
          .page-break-before {
            page-break-before: always;
            break-before: page;
          }
        }
      `}</style>
      </div>

      {/* PDF生成中のアニメーション */}
      {isGeneratingPDF && (
        <ProcessingAnimation
          title={t('report.generatingPdf')}
          description={t('report.generatingPdfDescription', { count: products.length })}
          steps={[
            t('report.pdfStep1'),
            t('report.pdfStep2'),
            t('report.pdfStep3')
          ]}
          currentStep={1}
        />
      )}
    </section>
  );
};
